import { useEffect, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { toast } from 'sonner'
import {
  Check,
  ArrowLeft,
  ArrowRight,
  AudioLines,
  ClipboardCopy,
  Mic,
  Lightbulb,
  PartyPopper,
} from 'lucide-react'
import { useRequireAuth } from '@/hooks/useRequireAuth'
import { useCreateApiKey } from '@/hooks/useApiKeys'
import { useAccountSetting } from '@/hooks/useSetting'
import { useLang } from '@/hooks/useLang'
import { VoiceSelector } from '@/components/account/VoiceSelector'
import { CodeBlock } from '@/components/ui/code-block'
import Logo from '@/components/Logo'
import { buildPhoneSaladPrompt } from '@/lib/agentPrompts'
import OpenClawIcon from '@/assets/openclaw-color.svg?react'
import OpenClawText from '@/assets/openclaw-text.svg?react'

export const Route = createFileRoute('/$lang/openclaw/setup')({
  validateSearch: (search: Record<string, unknown>) => ({
    step: typeof search.step === 'number' ? Math.min(Math.max(search.step, 1), STEPS.length) : 1,
  }),
  staticData: {
    title: 'Get Started - OpenClaw x PhoneSalad',
    description: 'Set up your AI voice agent in minutes. Pick a voice and connect to OpenClaw.',
  },
  component: SetupPage,
})

const STEPS = [
  { key: 'voice', icon: AudioLines, label: 'openclaw.setup.steps.voice.label' },
  { key: 'integration', icon: ClipboardCopy, label: 'openclaw.setup.steps.integration.label' },
] as const

function BrandHeader() {
  return (
    <div className="flex items-center gap-3 mb-6">
      <div className="flex items-center gap-1.5">
        <OpenClawIcon className="w-6 h-6" />
        <OpenClawText className="h-4 text-foreground" />
      </div>
      <span className="text-muted-foreground font-medium text-sm">x</span>
      <Logo size="sm" linkTo="" />
    </div>
  )
}

function StepSidebar({ currentStep }: { currentStep: number }) {
  const { t } = useTranslation('openclaw')
  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <BrandHeader />
        <Badge variant="secondary" className="uppercase text-xs font-bold tracking-widest">
          {t('openclaw.setup.badge')}
        </Badge>
        <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-foreground leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
          {currentStep === 0 && <>{t('openclaw.setup.voice.headline')} <br /><span className="text-primary">{t('openclaw.setup.voice.title')}</span></>}
          {currentStep === 1 && <>{t('openclaw.setup.integration.headline')} <br /><span className="text-primary">OpenClaw</span></>}
        </h1>
        <p className="text-muted-foreground leading-relaxed">
          {currentStep === 0 && t('openclaw.setup.voice.subheadline')}
          {currentStep === 1 && t('openclaw.setup.integration.subheadline')}
        </p>
      </div>

      {/* Step progress */}
      <div className="space-y-1 mb-4">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{t('openclaw.setup.step', { current: currentStep + 1, total: STEPS.length })}</span>
        </div>
        <Progress value={((currentStep + 1) / STEPS.length) * 100} className="h-1.5" />
      </div>

      <div className="space-y-5">
        {STEPS.map((step, i) => {
          const isCompleted = i < currentStep
          const isCurrent = i === currentStep
          const isFuture = i > currentStep

          return (
            <div key={step.key} className={`flex gap-4 items-start ${isFuture ? 'opacity-40' : ''}`}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm transition-all ${
                  isCompleted
                    ? 'bg-muted text-muted-foreground'
                    : isCurrent
                    ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {isCompleted ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              <div>
                <h4 className={`font-bold text-sm ${isCurrent ? 'text-primary' : ''}`}>
                  {t(step.label)}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {t(`openclaw.setup.steps.${step.key}.description`)}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Step 1: Voice Configuration — uses shared VoiceSelector
function VoiceStep({
  selectedVoice,
  onSelectVoice,
}: {
  selectedVoice: string | null
  onSelectVoice: (voiceId: string) => void
}) {
  const { t } = useTranslation('openclaw')
  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-bold text-lg mb-2">{t('openclaw.setup.voice.chooseTitle')}</h3>
        <p className="text-sm text-muted-foreground">
          {t('openclaw.setup.voice.chooseDescription')}
        </p>
      </div>

      <VoiceSelector
        value={selectedVoice}
        onChange={onSelectVoice}
        bare
      />

      <div className="rounded-xl bg-muted/30 border border-border/50 p-4 flex gap-3">
        <Mic className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="text-sm font-bold">{t('openclaw.setup.voice.help.title')}</p>
          <p className="text-xs text-muted-foreground">
            {t('openclaw.setup.voice.help.description')}
          </p>
        </div>
      </div>
    </div>
  )
}

// Step 2: Integration — skill prompt to paste into OpenClaw
function IntegrationStep({
  apiKey,
  selectedVoice,
}: {
  apiKey: string | null
  selectedVoice: string | null
}) {
  const { t } = useTranslation('openclaw')
  const lang = useLang()

  const skillPrompt = buildPhoneSaladPrompt(apiKey ?? 'YOUR_API_KEY')

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-bold text-lg mb-2">{t('openclaw.setup.integration.addTitle')}</h3>
        <p className="text-sm text-muted-foreground">
          {t('openclaw.setup.integration.addDescription')}
        </p>
      </div>

      <>
        <CodeBlock code={skillPrompt} />

          <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 flex gap-3">
            <Lightbulb className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-bold">{t('openclaw.setup.integration.howToTitle')}</p>
              <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
                <li>{t('openclaw.setup.integration.step1')}</li>
                <li>{t('openclaw.setup.integration.step2')}</li>
                <li>{t('openclaw.setup.integration.step3')}</li>
              </ol>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link
              to="/$lang/openclaw"
              params={{ lang }}
              className="rounded-xl bg-muted/30 border border-border/40 p-4 space-y-1 block hover:border-primary/40 transition-colors"
            >
              <h4 className="text-sm font-bold">{t('openclaw.setup.integration.learnMore')}</h4>
              <p className="text-xs text-muted-foreground">
                {t('openclaw.setup.integration.learnMoreDescription')}
              </p>
            </Link>
            <Link
              to="/$lang/openclaw/use-cases"
              params={{ lang }}
              className="rounded-xl bg-muted/30 border border-border/40 p-4 space-y-1 block hover:border-primary/40 transition-colors"
            >
              <h4 className="text-sm font-bold">{t('openclaw.setup.integration.useCaseIdeas')}</h4>
              <p className="text-xs text-muted-foreground">
                {t('openclaw.setup.integration.useCaseIdeasDescription')}
              </p>
            </Link>
          </div>
        </>
    </div>
  )
}

function SetupPage() {
  const { t } = useTranslation('openclaw')
  const { isAuthenticated } = useRequireAuth({ loginSearch: { from: 'openclaw' } })
  const lang = useLang()
  const navigate = useNavigate()
  const { step } = Route.useSearch()
  const currentStep = step - 1 // URL is 1-indexed, internal is 0-indexed

  const [selectedVoice, setSelectedVoice] = useState<string | null>(null)
  const [apiKey, setApiKey] = useState<string | null>(null)
  const [apiKeyError, setApiKeyError] = useState<boolean>(false)

  const createApiKeyMutation = useCreateApiKey()

  // Use new useAccountSetting hook for voice
  const { value: savedVoice, setAccountDefault: saveVoice, isUpdating } = useAccountSetting('call:agent_voice')

  // Auto-create API key on mount (only try once)
  useEffect(() => {
    if (apiKey || apiKeyError || createApiKeyMutation.isPending) return

    // Create a new "OpenClaw" key for this setup
    createApiKeyMutation.mutate('OpenClaw', {
      onSuccess: (response) => {
        setApiKey(response.apiKey)
      },
      onError: () => {
        setApiKeyError(true)
        toast.error('Failed to create API key. You can create one manually in your account settings.')
      },
    })
  }, [])

  const handleVoiceSelect = (voiceId: string) => {
    setSelectedVoice(voiceId)
  }

  const goToStep = (step: number) => {
    navigate({ to: '/$lang/openclaw/setup', params: { lang }, search: { step }, replace: false })
  }

  const handleNext = async () => {
    if (currentStep === 0 && selectedVoice) {
      try {
        await saveVoice(selectedVoice)
      } catch {
        // Continue even if save fails
      }
    }

    if (currentStep < STEPS.length - 1) {
      goToStep(step + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      goToStep(step - 1)
    }
  }

  const canProceed = () => {
    if (currentStep === 0) return !!selectedVoice
    return true
  }

  if (!isAuthenticated) return null

  const isLastStep = currentStep === STEPS.length - 1

  return (
    <section className="py-12 lg:py-20 px-4 relative overflow-hidden">
      {/* Background atmospheric elements */}
      <div className="absolute top-[-10%] right-[-5%] w-[400px] h-[400px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] left-[-5%] w-[300px] h-[300px] bg-primary/3 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto max-w-5xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start relative z-10">
          {/* Left Column */}
          <div className="lg:col-span-4">
            <StepSidebar currentStep={currentStep} />
          </div>

          {/* Right Column */}
          <div className="lg:col-span-8">
            <Card className="rounded-2xl shadow-lg border-0 bg-card/60 backdrop-blur-sm">
              <CardContent className="p-6 lg:p-10">
                <div className="space-y-8">
                  {currentStep === 0 && (
                    <VoiceStep
                      selectedVoice={selectedVoice ?? savedVoice ?? null}
                      onSelectVoice={handleVoiceSelect}
                    />
                  )}
                  {currentStep === 1 && (
                    <IntegrationStep
                      apiKey={apiKey}
                      selectedVoice={selectedVoice}
                    />
                  )}

                  {/* Navigation */}
                  <div className="flex items-center justify-between pt-4 border-t border-border/40">
                    {currentStep > 0 ? (
                      <Button
                        variant="ghost"
                        onClick={handleBack}
                        className="gap-2 text-muted-foreground hover:text-foreground"
                      >
                        <ArrowLeft className="h-4 w-4" />
                        {t('openclaw.setup.back')}
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        asChild
                        className="gap-2 text-muted-foreground hover:text-foreground"
                      >
                        <Link to="/$lang/openclaw" params={{ lang }}>
                          {t('openclaw.setup.skipSetup')}
                        </Link>
                      </Button>
                    )}

                    {!isLastStep ? (
                      <Button
                        onClick={handleNext}
                        disabled={!canProceed()}
                        className="gap-2 rounded-full px-8 font-bold shadow-lg shadow-primary/20"
                      >
                        {t('openclaw.setup.continue')}
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        asChild
                        className="gap-2 rounded-full px-8 font-bold shadow-lg shadow-primary/20"
                      >
                        <Link to="/$lang/account/api" params={{ lang }}>
                          <PartyPopper className="h-4 w-4" />
                          {t('openclaw.setup.done')}
                        </Link>
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  )
}
